// paquetes
import { useNavigate, useOutletContext } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
// funciones
import { ymdEnZona, hmEnZona, obtenerYMActualEnZona } from "../../../funcionesDatos/fechas";
// componentes
import ZonaCargando from '../../alertas/zonaCargando';
// estilos
import '../../../estilos/usuario.css';

// Página donde el vendedor marca horarios ocupados por fuera de Zervicy (clientes propios,
// compromisos personales). Los bloqueos se guardan en BloqueoAgenda (backend/routes/bloqueosAgenda.js)
// y se muestran junto a las reservas de Zervicy de este mes y el siguiente.

const DIAS_NOMBRES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const CLASES_SWAL  = { popup: 'mi-modal', confirmButton: 'mi-boton-confirmar', cancelButton: 'mi-boton-cancelar' };

// --- Helpers de fechas calendario (trabajamos con strings 'YYYY-MM-DD' para no correr días por UTC) ---
function ymdDe(y, m1a12, d) { return `${y}-${String(m1a12).padStart(2, "0")}-${String(d).padStart(2, "0")}`; }

function ymdFechaGuardada(fecha) {
    const texto = fecha ? String(fecha).slice(0, 10) : "";
    return /^\d{4}-\d{2}-\d{2}$/.test(texto) ? texto : "";
}

function crearMes(y, m1a12) {
    const totalDias = new Date(Date.UTC(y, m1a12, 0)).getUTCDate();
    const inicioSemana = (new Date(Date.UTC(y, m1a12 - 1, 1)).getUTCDay() + 6) % 7; // 0=Lun..6=Dom
    const celdas = Array(inicioSemana).fill(null);
    for (let d = 1; d <= totalDias; d++) celdas.push(ymdDe(y, m1a12, d));
    while (celdas.length % 7 !== 0) celdas.push(null);
    const nombre = new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m1a12 - 1, 15)));
    return { y, m: m1a12, nombre, celdas };
}

function formatearDia(ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Intl.DateTimeFormat('es-CO', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

// Días (YYYY-MM-DD) que ocupa una reserva de Zervicy. Mismo criterio que Reserva.jsx:
// flexible -> `fecha`; por horas -> inicio/fin en la zona; por días -> fecha..fechaFin.
function diasDeReserva(r, zona) {
    if (r?.modoAgenda === 'flexible' && r?.fecha) return [ymdFechaGuardada(r.fecha)];
    let desde = r?.inicio ? ymdEnZona(r.inicio, zona) : ymdFechaGuardada(r?.fecha);
    let hasta = r?.fin ? ymdEnZona(r.fin, zona) : (r?.fechaFin ? ymdFechaGuardada(r.fechaFin) : desde);
    if (!desde) return [];
    if (!hasta || hasta < desde) hasta = desde;
    const dias = [];
    for (let actual = desde; actual <= hasta && dias.length < 62;) {
        dias.push(actual);
        const [y, m, d] = actual.split('-').map(Number);
        actual = new Date(Date.UTC(y, m - 1, d + 1, 12)).toISOString().slice(0, 10);
    }
    return dias;
}

function textoHoraReserva(r, zona) {
    if (r?.unidadEntrega === 'Dias') return 'Día completo';
    const inicio = (r?.horaConfirmada && r.horaConfirmada !== 'NA') ? r.horaConfirmada
        : (r?.horaSolicitada && r.horaSolicitada !== 'NA') ? r.horaSolicitada
        : (r?.inicio ? hmEnZona(r.inicio, zona) : '');
    const fin = r?.fin ? hmEnZona(r.fin, zona) : '';
    if (!inicio) return 'Hora por confirmar';
    return fin && fin !== inicio ? `${inicio} – ${fin}` : inicio;
}



function AgendaVendedor() {
    const { usuario, cargandoUsuario } = useOutletContext() || {}; // variables que vienen de direccionamiento
    const navigate = useNavigate();

    // Variables
    const [cargando, setCargando]       = useState(true);
    const [guardando, setGuardando]     = useState(false);
    const cargandoFinal                 = Boolean(cargandoUsuario) || Boolean(cargando);
    const [ahora, setAhora]             = useState(null);
    const [zonaHoraria, setZonaHoraria] = useState("America/Bogota");
    const [servicios, setServicios]     = useState([]);
    const [bloqueos, setBloqueos]       = useState([]);
    const [reservas, setReservas]       = useState([]);

    // Formulario
    const [seleccion, setSeleccion]                   = useState([]); // ['YYYY-MM-DD', ...]
    const [diaCompleto, setDiaCompleto]               = useState(true);
    const [horaInicio, setHoraInicio]                 = useState("08:00");
    const [horaFin, setHoraFin]                       = useState("12:00");
    const [todosServicios, setTodosServicios]         = useState(true);
    const [serviciosElegidos, setServiciosElegidos]   = useState([]);
    const [motivo, setMotivo]                         = useState("");

    // Este mes y el siguiente, en la zona del vendedor (respetando la fecha de pruebas del backend)
    const meses = useMemo(() => {
        if (!ahora) return [];
        const { y, m } = obtenerYMActualEnZona(zonaHoraria, ahora);
        return [crearMes(y, m), crearMes(m === 12 ? y + 1 : y, m === 12 ? 1 : m + 1)];
    }, [ahora, zonaHoraria]);

    const hoyYmd = ahora ? ymdEnZona(ahora, zonaHoraria) : "";


    const cargarAgenda = useCallback(async () => {
        if (!usuario) return;
        try {
            setCargando(true);
            const resAhora = await axios.post("/obtener-ahora").catch(() => null);
            const ahoraUsar = resAhora?.data?.ahora ? new Date(resAhora.data.ahora) : new Date();
            setAhora(ahoraUsar); // antes de pedir la agenda: si esa petición falla, el calendario igual se pinta

            // Primera carga con la zona por defecto; el backend devuelve la real del vendedor.
            const { y, m } = obtenerYMActualEnZona(zonaHoraria, ahoraUsar);
            const ySig = m === 12 ? y + 1 : y;
            const mSig = m === 12 ? 1 : m + 1;
            const desde = ymdDe(y, m, 1);
            const hasta = ymdDe(ySig, mSig, new Date(Date.UTC(ySig, mSig, 0)).getUTCDate());

            const res = await axios.post("/bloqueos-agenda/vendedor", { params: { desde, hasta } });
            setZonaHoraria(res?.data?.zonaHoraria || "America/Bogota");
            setServicios(res?.data?.servicios || []);
            setBloqueos(res?.data?.bloqueos || []);
            setReservas(res?.data?.reservas || []);
        }
        catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.mensaje || 'No pudimos cargar tu agenda.', customClass: CLASES_SWAL });
        }
        finally { setCargando(false); }
    }, [usuario, zonaHoraria]);


    useEffect(() => {
        if (cargandoUsuario) return; // Espera a que Direccionamiento termine de cargar sesión
        if (!usuario) { navigate("/iniciar-sesion"); return; }
        cargarAgenda();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario, cargandoUsuario]);


    // Índices por día para pintar el calendario y el detalle
    const bloqueosPorDia = useMemo(() => {
        const mapa = {};
        bloqueos.forEach((b) => {
            const dia = ymdFechaGuardada(b.fecha);
            if (dia) (mapa[dia] = mapa[dia] || []).push(b);
        });
        return mapa;
    }, [bloqueos]);

    const reservasPorDia = useMemo(() => {
        const mapa = {};
        reservas.forEach((r) => diasDeReserva(r, zonaHoraria).forEach((dia) => { (mapa[dia] = mapa[dia] || []).push(r); }));
        return mapa;
    }, [reservas, zonaHoraria]);

    const nombreServicio = (idServicio) => servicios.find((s) => s.idServicio === idServicio)?.nombre || 'Servicio';

    function textoServiciosBloqueo(b) {
        if (!b?.idServicios?.length) return 'Todos tus servicios';
        return b.idServicios.map(nombreServicio).join(', ');
    }


    function alternarDia(ymd) {
        setSeleccion((prev) => prev.includes(ymd) ? prev.filter((d) => d !== ymd) : [...prev, ymd].sort());
    }

    function alternarServicio(idServicio) {
        setServiciosElegidos((prev) => prev.includes(idServicio) ? prev.filter((id) => id !== idServicio) : [...prev, idServicio]);
    }


    async function guardarBloqueo() {
        if (seleccion.length === 0) return;
        if (!diaCompleto && horaFin <= horaInicio) {
            return Swal.fire({ icon: 'warning', title: 'Revisa las horas', text: 'La hora final debe ser posterior a la inicial.', customClass: CLASES_SWAL });
        }
        if (!todosServicios && serviciosElegidos.length === 0) {
            return Swal.fire({ icon: 'warning', title: 'Elige servicios', text: 'Selecciona al menos un servicio o marca "Todos mis servicios".', customClass: CLASES_SWAL });
        }

        try {
            setGuardando(true);
            await axios.post("/bloqueos-agenda/crear", {
                params: {
                    fechas: seleccion,
                    diaCompleto,
                    horaInicio,
                    horaFin,
                    idServicios: todosServicios ? [] : serviciosElegidos,
                    motivo,
                    origen: "web",
                }
            });
            setSeleccion([]);
            setMotivo("");
            await cargarAgenda();
            Swal.fire({ icon: 'success', title: 'Horario bloqueado', text: 'Los clientes ya no podrán reservar en ese horario.', customClass: CLASES_SWAL, timer: 2200, showConfirmButton: false });
        }
        catch (err) {
            const data = err?.response?.data;
            const choques = Array.isArray(data?.choques) ? data.choques : [];
            const detalle = choques.length > 0
                ? '<ul class="text-start mt-2 mb-0">' + choques.slice(0, 6).map((c) =>
                    `<li>${formatearDia(ymdEnZona(c.inicio, zonaHoraria))} · ${hmEnZona(c.inicio, zonaHoraria)}${c.fin ? ' – ' + hmEnZona(c.fin, zonaHoraria) : ''} · ${nombreServicio(c.idServicio)}</li>`
                ).join('') + '</ul>'
                : '';
            Swal.fire({
                icon: 'error',
                title: choques.length > 0 ? 'Horario con reservas' : 'Error',
                html: `${data?.mensaje || 'No pudimos guardar el bloqueo. Vuelve a intentar.'}${detalle}`,
                customClass: CLASES_SWAL,
            });
        }
        finally { setGuardando(false); }
    }


    async function eliminarBloqueo(b) {
        const confirmacion = await Swal.fire({
            icon: 'question',
            title: '¿Liberar este horario?',
            text: 'Los clientes podrán volver a reservarlo.',
            showCancelButton: true,
            confirmButtonText: 'Sí, liberar',
            cancelButtonText: 'Cancelar',
            customClass: CLASES_SWAL,
        });
        if (!confirmacion.isConfirmed) return;

        try {
            setGuardando(true);
            await axios.post("/bloqueos-agenda/eliminar", { params: { idBloqueo: b.idBloqueo } });
            await cargarAgenda();
        }
        catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.mensaje || 'No pudimos eliminar el bloqueo.', customClass: CLASES_SWAL });
        }
        finally { setGuardando(false); }
    }


    function verReserva(idReserva) {
        navigate("/vendedor/reserva", { state: { idReserva, idUsuario: usuario?.idUsuario, quienEs: usuario?.modo } });
    }


    // Días que se listan en el detalle: los seleccionados, o todos los que tienen algo desde hoy.
    const diasDetalle = useMemo(() => {
        if (seleccion.length > 0) return seleccion;
        return [...new Set([...Object.keys(bloqueosPorDia), ...Object.keys(reservasPorDia)])]
            .filter((d) => d >= hoyYmd)
            .sort();
    }, [seleccion, bloqueosPorDia, reservasPorDia, hoyYmd]);



    function renderMes(mes) {
        return (
            <div className="col-12 col-md-6 mb-4" key={`${mes.y}-${mes.m}`}>
                <div className="text-capitalize fw-semibold mb-2 text-dark">{mes.nombre}</div>
                <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                    {DIAS_NOMBRES.map((d) => (
                        <div key={`h-${d}`} className="text-center text-body-secondary" style={{ fontSize: 12 }}>{d}</div>
                    ))}

                    {mes.celdas.map((ymd, idx) => {
                        if (!ymd) return <div key={`e-${idx}`} />;

                        const pasado        = ymd < hoyYmd;
                        const seleccionado  = seleccion.includes(ymd);
                        const bloqueosDia   = bloqueosPorDia[ymd] || [];
                        const reservasDia   = reservasPorDia[ymd] || [];
                        const bloqueadoTodo = bloqueosDia.some((b) => b.diaCompleto && !b.idServicios?.length);

                        return (
                            <button
                                key={ymd}
                                type="button"
                                className={`btn btn-sm ${seleccionado ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{
                                    padding: '4px 0 2px',
                                    minHeight: 44,
                                    ...(pasado ? { border: '1px solid var(--color-disabled-border)', color: 'var(--color-disabled-text)', background: 'var(--color-disabled-bg)', cursor: 'not-allowed' } : {}),
                                    ...(!pasado && !seleccionado && bloqueadoTodo ? { background: 'var(--naranja-muy-suave)', borderColor: 'var(--naranja-claro-borde)', color: 'var(--marron-oscuro)' } : {}),
                                }}
                                disabled={pasado}
                                onClick={() => alternarDia(ymd)}
                                title={[
                                    bloqueosDia.length ? `${bloqueosDia.length} bloqueo(s)` : '',
                                    reservasDia.length ? `${reservasDia.length} reserva(s) de Zervicy` : '',
                                ].filter(Boolean).join(' · ')}
                            >
                                <div style={{ lineHeight: 1.1 }}>{Number(ymd.slice(8))}</div>
                                <div className="d-flex justify-content-center gap-1" style={{ height: 8 }}>
                                    {bloqueosDia.length > 0 ? <span style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 2, backgroundColor: 'var(--naranja-cta)' }} /> : null}
                                    {reservasDia.length > 0 ? <span style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 2, backgroundColor: 'var(--azul-logo-claro)' }} /> : null}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }



    return (
    <div>
    { (cargandoFinal && !ahora) ? <ZonaCargando />
    : usuario ?
    <div className='d-flex flex-row'>
        <div className="flex-grow-1 container-fluid contenedorAdaptador">
            {/* Contenedor título */}
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom border-dark">
                <div className="container">
                    <h1 className="h2 text-dark mb-1">Agenda</h1>
                    <p className="text-body-secondary mb-0">
                        Marca los horarios que ya tienes ocupados por fuera de Zervicy. Los clientes no podrán reservarlos.
                    </p>
                </div>
            </div>

            <div className="container pb-5">
                <div className="row g-4">
                    {/* CALENDARIOS + DETALLE */}
                    <div className="col-12 col-lg-7">
                        <div className="d-flex flex-wrap gap-3 mb-3 text-body-secondary" style={{ fontSize: 13 }}>
                            <span><span className="d-inline-block me-1" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--naranja-cta)' }} />Bloqueado por ti</span>
                            <span><span className="d-inline-block me-1" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--azul-logo-claro)' }} />Reserva de Zervicy</span>
                            <span>Horarios en zona: <span className="fw-semibold">{zonaHoraria}</span></span>
                        </div>

                        <div className="row">{meses.map(renderMes)}</div>

                        {/* DETALLE POR DÍA */}
                        <h2 className="h5 text-dark mt-2 mb-3">
                            {seleccion.length > 0 ? 'Días seleccionados' : 'Próximos horarios ocupados'}
                        </h2>

                        {diasDetalle.length === 0 ? (
                            <div className="alert alert-light border">No tienes horarios ocupados en estos dos meses.</div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {diasDetalle.map((dia) => {
                                    const bloqueosDia = bloqueosPorDia[dia] || [];
                                    const reservasDia = reservasPorDia[dia] || [];
                                    return (
                                        <div key={dia} className="border rounded-3 p-3">
                                            <div className="fw-semibold text-dark text-capitalize mb-2">{formatearDia(dia)}</div>

                                            {bloqueosDia.length === 0 && reservasDia.length === 0 ? (
                                                <div className="text-body-secondary" style={{ fontSize: 14 }}>Libre</div>
                                            ) : null}

                                            {bloqueosDia.map((b) => (
                                                <div key={b.idBloqueo} className="d-flex justify-content-between align-items-start gap-2 py-2 border-top">
                                                    <div style={{ fontSize: 14 }}>
                                                        <span className="badge me-2" style={{ backgroundColor: 'var(--naranja-cta)' }}>Bloqueado</span>
                                                        <span className="fw-semibold">{b.diaCompleto ? 'Día completo' : `${b.horaInicio} – ${b.horaFin}`}</span>
                                                        <div className="text-body-secondary">{textoServiciosBloqueo(b)}</div>
                                                        {b.motivo ? <div className="text-body-secondary fst-italic">{b.motivo}</div> : null}
                                                    </div>
                                                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={guardando} onClick={() => eliminarBloqueo(b)}>
                                                        <i className="bi bi-trash"></i> Liberar
                                                    </button>
                                                </div>
                                            ))}

                                            {reservasDia.map((r) => (
                                                <div key={`${r._id}-${dia}`} className="d-flex justify-content-between align-items-start gap-2 py-2 border-top">
                                                    <div style={{ fontSize: 14 }}>
                                                        <span className="badge me-2" style={{ backgroundColor: 'var(--azul-logo-claro)' }}>Zervicy</span>
                                                        <span className="fw-semibold">{textoHoraReserva(r, zonaHoraria)}</span>
                                                        <div className="text-body-secondary">{nombreServicio(r.idServicio)} · {r.tipoPaquete}</div>
                                                    </div>
                                                    {r.estado === 'confirmado' || r.tipoHold === 'confirmacionHorario' ? (
                                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => verReserva(r._id)}>Ver</button>
                                                    ) : (
                                                        <span className="text-body-secondary" style={{ fontSize: 12 }}>En pago</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* FORMULARIO DE BLOQUEO */}
                    <div className="col-12 col-lg-5">
                        <div className="border rounded-3 p-3 p-md-4" style={{ position: 'sticky', top: 16 }}>
                            <h2 className="h5 text-dark mb-3">Bloquear horario</h2>

                            {/* Días */}
                            <div className="mb-3">
                                <div className="fw-semibold mb-2 text-dark">Días</div>
                                {seleccion.length === 0 ? (
                                    <div className="text-body-secondary" style={{ fontSize: 14 }}>Toca uno o varios días en el calendario.</div>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                        {seleccion.map((d) => (
                                            <span key={d} className="badge rounded-pill text-bg-primary text-capitalize d-inline-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                                                {formatearDia(d)}
                                                <button type="button" className="btn-close btn-close-white" style={{ fontSize: 8 }} aria-label="Quitar" onClick={() => alternarDia(d)} />
                                            </span>
                                        ))}
                                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setSeleccion([])}>Limpiar</button>
                                    </div>
                                )}
                            </div>

                            {/* Horario */}
                            <div className="mb-3">
                                <div className="fw-semibold mb-2 text-dark">Horario</div>
                                <div className="form-check">
                                    <input className="form-check-input" type="radio" id="bloqueoDiaCompleto" checked={diaCompleto} onChange={() => setDiaCompleto(true)} />
                                    <label className="form-check-label" htmlFor="bloqueoDiaCompleto">Día completo</label>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="radio" id="bloqueoRango" checked={!diaCompleto} onChange={() => setDiaCompleto(false)} />
                                    <label className="form-check-label" htmlFor="bloqueoRango">Solo algunas horas</label>
                                </div>
                                {diaCompleto ? null : (
                                    <div className="d-flex gap-2 mt-2 align-items-center">
                                        <input type="time" className="form-control" step={900} value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} aria-label="Hora inicial" />
                                        <span className="text-body-secondary">a</span>
                                        <input type="time" className="form-control" step={900} value={horaFin} onChange={(e) => setHoraFin(e.target.value)} aria-label="Hora final" />
                                    </div>
                                )}
                            </div>

                            {/* Servicios */}
                            <div className="mb-3">
                                <div className="fw-semibold mb-2 text-dark">¿A qué servicios afecta?</div>
                                <div className="form-check">
                                    <input className="form-check-input" type="radio" id="bloqueoTodos" checked={todosServicios} onChange={() => setTodosServicios(true)} />
                                    <label className="form-check-label" htmlFor="bloqueoTodos">Todos mis servicios</label>
                                </div>
                                <div className="form-check">
                                    <input className="form-check-input" type="radio" id="bloqueoAlgunos" checked={!todosServicios} onChange={() => setTodosServicios(false)} disabled={servicios.length === 0} />
                                    <label className="form-check-label" htmlFor="bloqueoAlgunos">Solo algunos</label>
                                </div>
                                {todosServicios ? null : (
                                    <div className="mt-2 ps-2 d-flex flex-column gap-1">
                                        {servicios.map((s) => (
                                            <div className="form-check" key={s.idServicio}>
                                                <input className="form-check-input" type="checkbox" id={`srv-${s.idServicio}`} checked={serviciosElegidos.includes(s.idServicio)} onChange={() => alternarServicio(s.idServicio)} />
                                                <label className="form-check-label" htmlFor={`srv-${s.idServicio}`}>
                                                    {s.nombre}{s.estado !== 'Aprobado' ? <span className="text-body-secondary"> ({s.estado})</span> : null}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Motivo */}
                            <div className="mb-3">
                                <label className="fw-semibold mb-2 text-dark" htmlFor="bloqueoMotivo">Nota <span className="fw-normal text-body-secondary">(opcional, solo tú la ves)</span></label>
                                <input id="bloqueoMotivo" type="text" className="form-control" maxLength={200} placeholder="Ej: Cliente por WhatsApp" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                            </div>

                            <button type="button" className="btn btn-primary w-100 rounded-pill" disabled={seleccion.length === 0 || guardando} onClick={guardarBloqueo}>
                                {guardando ? 'Guardando…' : seleccion.length > 1 ? `Bloquear ${seleccion.length} días` : 'Bloquear horario'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    : null
    }
    </div>
    );
}



export default AgendaVendedor;
